import React from 'react';
import {useIntl} from 'react-intl';
import {Data, Element, GridContainer, IconWrapper, Row, Title, TotalsLine} from './ProjectScopingTotals_styled';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {ValueCell} from '../../../../components/initial-plan/ValueCell';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {ProjectScopingTotalsTooltips, TimeEntriesValueTooltip} from './ProjectScopingTotalsTooltips';
import {ExpandableIcon} from 'web-components';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {BUDGET_TYPE} from '../../../../constants';
import {remapId} from '../../../shared/util/FinancialInternationalisationUtil';

/**
 * Create the project scoping header section in a line (collapsed)
 *
 * @returns JSX Project Scoping Totals Line Component
 */
const ProjectScopingTotalsBox = () => {
	const intl = useIntl();

	const {totalsExpanded, toggleTotalsExpanded, totalsData, settings, projectData} = useProjectTotals();
	const hasRevenueRecognition = hasFeatureFlag('revenue_recognition');
	const isFixedPriceV2Project = projectData.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 && !hasRevenueRecognition;

	return (
		<GridContainer data-userpilot="project-totals-header">
			<TotalsLine key="totals-box">
				<Row>
					<Element column>
						<Title>Start date</Title>
						<Data small>
							<ValueCell noJustify value={totalsData.startDateString} />
						</Data>
					</Element>

					<Element column>
						<Title>End date</Title>
						<Data small>
							<ValueCell noJustify value={totalsData.endDateString} />
						</Data>
					</Element>

					<Element column>
						<Title>{intl.formatMessage({id: 'common.completion'})}</Title>
						<Data small>
							<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={totalsData.progress} />
						</Data>
					</Element>

					{settings.hasFinancialAccess && (
						<Element column>
							<Title>{intl.formatMessage({id: remapId('project_budget.plan_revenue')})}</Title>
							<Data small>
								<ValueCell
									noJustify
									type={ValueCell.VALUE_TYPE.PRICE}
									value={totalsData.plannedRevenue}
									currencySymbol={settings.currencySymbol}
								/>
							</Data>
						</Element>
					)}

					{settings.hasFinancialAccess && (
						<Element column>
							<Title>{intl.formatMessage({id: remapId('project_budget.actual_revenue_no_expenses')})}</Title>
							<Data small>
								<ForecastTooltip
									placement={'bottom'}
									content={ProjectScopingTotalsTooltips()}
									disabled={isFixedPriceV2Project}
								>
									<ValueCell
										noJustify
										type={ValueCell.VALUE_TYPE.PRICE}
										value={totalsData.actualRevenue}
										currencySymbol={settings.currencySymbol}
									/>
								</ForecastTooltip>
							</Data>
						</Element>
					)}

					<Element column>
						<Title>{intl.formatMessage({id: 'common.estimate'})}</Title>
						<Data small>
							<ValueCell
								noJustify
								type={totalsData.isEstimatedInHours ? ValueCell.VALUE_TYPE.TIME : ValueCell.VALUE_TYPE.POINTS}
								value={totalsData.estimate}
								currencySymbol={settings.currencySymbol}
							/>
						</Data>
					</Element>

					{!hasFeatureFlag('no_time_reg') && (
						<Element column>
							<Title>{intl.formatMessage({id: 'common.time_entries'})}</Title>
							<Data small>
								<ForecastTooltip placement={'left'} content={TimeEntriesValueTooltip()}>
									<ValueCell
										noJustify
										type={ValueCell.VALUE_TYPE.TIME}
										value={totalsData.timeEntries}
										currencySymbol={settings.currencySymbol}
									/>
								</ForecastTooltip>
							</Data>
						</Element>
					)}

					<Element column>
						<Title>{intl.formatMessage({id: 'common.remaining'})}</Title>
						<Data small>
							<ValueCell
								noJustify
								type={totalsData.isEstimatedInHours ? ValueCell.VALUE_TYPE.TIME : ValueCell.VALUE_TYPE.POINTS}
								value={totalsData.remaining}
								currencySymbol={settings.currencySymbol}
							/>
						</Data>
					</Element>

					<Element column>
						<Title>{intl.formatMessage({id: 'project_scoping.difference_forecast'})}</Title>
						<Data small>
							<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData.difference} />
						</Data>
					</Element>
					<IconWrapper>
						<ExpandableIcon isOpen={totalsExpanded} onClick={toggleTotalsExpanded} />
					</IconWrapper>
				</Row>
			</TotalsLine>
		</GridContainer>
	);
};

export default ProjectScopingTotalsBox;
